/**
 * Drawer sections 1–2 (#1911): what the mail says, and who it reaches.
 *
 * ── THE TWO TITLES ARE NOT ONE TITLE ────────────────────────────────────────
 * `task_title` is the operator's own label — "Claude Code Opus4.6 下架通知" —
 * and it is what a human scans a list of forty notices by. `subject` is the
 * mail's Subject header and it is read by people who pay us. #1877 had one
 * field doing both jobs, so an internal label like "第三批下架，等法务确认"
 * was one careless save away from being the first line of a customer's inbox.
 * They are separate inputs, separately labelled, and neither is derived from
 * the other.
 *
 * ── THE BODY IS THE OPERATOR'S PROSE ────────────────────────────────────────
 * Nothing is assembled around it: no headline, no model list, no replacement
 * paragraph slotted in by a template. The starting-draft picker below writes
 * the stored template's subject and body INTO the draft once, and after that
 * they are ordinary text — the template is a starting point, not a frame the
 * operator writes inside.
 */
import { useState } from 'react';
import type { EmailTemplate, ModelRecord } from '@nadoutong/contracts';
import {
  EMAIL_NOTICE_PLACEHOLDERS,
  EMAIL_TEMPLATE_PLACEHOLDERS,
} from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, FormField, Input, Select, Textarea } from '../../ui/index.ts';
import type { AudiencePreviewControls } from './AudiencePreviewPanel.tsx';
import { NoticeAudienceSection } from './NoticeAudienceSection.tsx';
import { SectionCaption } from './SectionCaption.tsx';
import { type NoticeDraft, draftUnknownPlaceholders } from './noticeDraft.ts';
import { templateNameKey } from './templateNames.ts';

interface Props {
  readonly draft: NoticeDraft;
  readonly onChange: (next: NoticeDraft) => void;
  readonly models: readonly ModelRecord[];
  readonly modelsLoading: boolean;
  readonly templates: readonly EmailTemplate[];
  /** Editing is a draft-only act; a sent notice renders read-only. */
  readonly disabled: boolean;
  /** Section 2's "who does this reach" control — see `AudiencePreviewPanel`. */
  readonly preview: AudiencePreviewControls;
}

/**
 * Templates a notice may start from.
 *
 * Filtered by whether the template's allowed placeholders fit inside a
 * notice's, rather than by a hardcoded `notice.*` prefix. A balance-alert
 * template legitimately says `{{ balance }}`, and loading one here would put a
 * placeholder into copy that the notice endpoints refuse — the operator would
 * be offered a starting draft that cannot be saved. The rule is derived from
 * the two allowlists, so a sixth template key lands on the correct side of it
 * without anyone remembering to look.
 */
export function noticeStartableTemplates(
  templates: readonly EmailTemplate[],
): readonly EmailTemplate[] {
  const allowed = new Set(EMAIL_NOTICE_PLACEHOLDERS);
  return templates.filter((template) =>
    (EMAIL_TEMPLATE_PLACEHOLDERS[template.key] ?? []).every((name) => allowed.has(name)),
  );
}

export function NoticeDraftForm({
  draft,
  onChange,
  models,
  modelsLoading,
  templates,
  disabled,
  preview,
}: Props): JSX.Element {
  const t = useT();
  const [templateKey, setTemplateKey] = useState<string>('');
  const startable = noticeStartableTemplates(templates);
  const unknown = draftUnknownPlaceholders(draft);

  const loadTemplate = (): void => {
    const chosen = startable.find((template) => template.key === templateKey);
    if (chosen === undefined) return;
    // Subject and body only. `task_title`, the audience and the reminder are
    // this notice's own and have no business being overwritten by stored copy.
    onChange({ ...draft, subject: chosen.subject, body: chosen.body });
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-2" data-testid="email-notice-section-copy">
        <SectionCaption
          index={1}
          title={t('email_notices.section.copy')}
          hint={t('email_notices.section.copy_hint')}
        />

        <FormField
          label={t('email_notices.field.task_title')}
          fieldId="notice-task-title"
          hint={t('email_notices.field.task_title_hint')}
          required
        >
          <Input
            id="notice-task-title"
            value={draft.taskTitle}
            disabled={disabled}
            onChange={(event) => onChange({ ...draft, taskTitle: event.target.value })}
            data-testid="email-notice-task-title"
          />
        </FormField>

        <div className="flex flex-col gap-2 rounded border border-line-soft bg-well px-3 py-2.5">
          <FormField
            label={t('email_notices.template.load_label')}
            fieldId="notice-template-key"
            hint={t('email_notices.template.load_hint')}
          >
            <Select
              id="notice-template-key"
              value={templateKey}
              onValueChange={setTemplateKey}
              options={startable.map((template) => ({
                value: template.key,
                label: t(templateNameKey(template.key)),
              }))}
              placeholder={t('email_notices.template.load_placeholder')}
              disabled={disabled || startable.length === 0}
              testId="email-notice-template-key"
            />
          </FormField>
          <div>
            <Button
              size="sm"
              variant="secondary"
              disabled={disabled || templateKey === ''}
              onClick={loadTemplate}
              data-testid="email-notice-template-load"
            >
              {t('email_notices.template.load_action')}
            </Button>
          </div>
        </div>

        <FormField
          label={t('email_notices.field.subject')}
          fieldId="notice-subject"
          hint={t('email_notices.field.subject_hint')}
          required
        >
          <Input
            id="notice-subject"
            value={draft.subject}
            disabled={disabled}
            onChange={(event) => onChange({ ...draft, subject: event.target.value })}
            data-testid="email-notice-subject"
          />
        </FormField>

        <FormField
          label={t('email_notices.field.body')}
          fieldId="notice-body"
          required
          hint={t('email_notices.field.body_hint', {
            placeholders: EMAIL_NOTICE_PLACEHOLDERS.map((name) => `{{ ${name} }}`).join('  '),
          })}
          error={
            unknown.length === 0
              ? null
              : t('email_notices.placeholder_unknown', { names: unknown.join(', ') })
          }
        >
          <Textarea
            id="notice-body"
            rows={8}
            value={draft.body}
            disabled={disabled}
            aria-describedby={unknown.length === 0 ? undefined : 'notice-body-error'}
            onChange={(event) => onChange({ ...draft, body: event.target.value })}
            data-testid="email-notice-body"
          />
        </FormField>

        <FormField
          label={t('email_notices.field.remind_on')}
          fieldId="notice-remind-on"
          hint={t('email_notices.field.remind_on_hint')}
        >
          <Input
            id="notice-remind-on"
            type="date"
            mono
            value={draft.remindOn}
            disabled={disabled}
            onChange={(event) => onChange({ ...draft, remindOn: event.target.value })}
            data-testid="email-notice-remind-on"
          />
        </FormField>
      </section>

      <NoticeAudienceSection
        audience={draft.audience}
        onChange={(audience) => onChange({ ...draft, audience })}
        models={models}
        modelsLoading={modelsLoading}
        disabled={disabled}
        preview={preview}
      />
    </div>
  );
}
